import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { LearningOpportunity, LearningOpportunityCreate } from '~/shared/data-contract/learning-opportunities';
import { learningOpportunitySchema } from '~/shared/data-contract/learning-opportunities';

import { invalidateLearningOpportunityCaches } from './cache-invalidation';
import { learningOpportunityPathnames } from './query-keys';

type LearningOpportunityUpdateMutationResult = UseMutationResult<
    LearningOpportunity,
    Error,
    Partial<LearningOpportunityCreate>
>;

export const useUpdateLearningOpportunityMutation = <T extends 'patchJson'>(
    operationName: T,
    id: string,
): LearningOpportunityUpdateMutationResult => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (learningOpportunity) => {
            const result = await validatingCobrainerApiFetcher.patchJson(learningOpportunitySchema, {
                pathname: learningOpportunityPathnames.detail(id),
                payload: learningOpportunity,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        mutationKey: [learningOpportunityPathnames.pathname, operationName],
        onSuccess: () => {
            invalidateLearningOpportunityCaches(queryClient);
        },
    });
};
